/**
 * Custom File Uploader
 * Drag-and-drop file upload with shape-aware preview masking
 */

(() => {
  'use strict';

  const API_ENDPOINT = window.POPSIGNS_API_ENDPOINT || 'https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging';
  const DEBUG = window.PITCHPRINT_DEBUG || false;
  const log = (...args) => DEBUG && console.log('[FileUploader]', ...args);

  class CustomFileUploader {
    constructor(container) {
      this.container = container;
      this.previewRenderer = new window.FileUploadPreviewRenderer();
      this.stateManager = window.PitchPrintStateManager;

      this.state = {
        mode: 'upload_now', // 'upload_now' or 'send_later'
        sides: 'single', // 'single' or 'double'
        shape: this.detectShape(),
        activeTab: 'front', // Track which side is shown in preview
        files: {
          front: null,
          back: null
        },
        uploads: {
          front: null,
          back: null
        },
        uploadProgress: {
          front: 0,
          back: 0
        }
      };

      this.init();
    }

    init() {
      log('Initializing file uploader');
      this.state.sides = this.detectSides(); // Auto-detect on init
      this.render();
      this.attachEventListeners();
      this.watchVariantChanges(); // Watch for variant changes
      this.watchDesignMethodChanges(); // Watch for design method toggle
    }

    detectShape() {
      // First check if this is a longboard variant that needs size-based detection
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        // For longboard variants, ALWAYS check size first before shape dropdown
        if (variantTitle.includes('longboard')) {
          // Try multiple possible size option names
          const sizeSelect = document.querySelector('select[name="options[Choose Your Size]"]') ||
                            document.querySelector('select[name="options[Size]"]');

          if (sizeSelect && sizeSelect.value) {
            const sizeValue = sizeSelect.value.toLowerCase();
            log('Checking size value for longboard:', sizeValue);

            if (sizeValue.includes('36') || sizeValue.includes('36x10')) {
              log('Shape detected from size: Longboard Shape (36x10)');
              return 'Longboard Shape';
            }
            if (sizeValue.includes('20') || sizeValue.includes('20x8')) {
              log('Shape detected from size: Longboard Mini (20x8)');
              return 'Longboard Mini';
            }
          }
          // Default to Longboard Shape if size not specified
          log('Shape detected from variant title: Longboard Shape (default, no size found)');
          return 'Longboard Shape';
        }
      }

      // For non-longboard variants, check "Choose Your Shape" dropdown
      const shapeSelect = document.querySelector('select[name="options[Choose Your Shape]"]');
      if (shapeSelect && shapeSelect.value) {
        // If the dropdown says "Longboard Shape", we need to check size for Mini vs Regular
        if (shapeSelect.value === 'Longboard Shape') {
          const sizeSelect = document.querySelector('select[name="options[Choose Your Size]"]') ||
                            document.querySelector('select[name="options[Size]"]');

          if (sizeSelect && sizeSelect.value) {
            const sizeValue = sizeSelect.value.toLowerCase();
            log('Checking size value for longboard shape:', sizeValue);

            if (sizeValue.includes('20') || sizeValue.includes('20x8')) {
              log('Shape detected from size: Longboard Mini (20x8)');
              return 'Longboard Mini';
            }
            // 36x10 or other sizes default to Longboard Shape
            log('Shape detected from size: Longboard Shape (36x10 or default)');
            return 'Longboard Shape';
          }
        }

        log('Shape detected from dropdown:', shapeSelect.value);
        return shapeSelect.value;
      }

      // Try to detect from variant title for other shapes
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        if (variantTitle.includes('circle')) {
          log('Shape detected from variant title: Circle Shape');
          return 'Circle Shape';
        }
        if (variantTitle.includes('classic')) {
          log('Shape detected from variant title: Classic Shape');
          return 'Classic Shape';
        }
      }

      // Check all option selects for shape keywords
      const options = document.querySelectorAll('[name^="options["]');
      for (const option of options) {
        const value = option.value ? option.value.toLowerCase() : '';
        if (value.includes('circle')) {
          log('Shape detected from options: Circle Shape');
          return 'Circle Shape';
        }
        if (value.includes('classic')) {
          log('Shape detected from options: Classic Shape');
          return 'Classic Shape';
        }
      }

      // Default to Classic Shape
      log('Shape defaulting to: Classic Shape');
      return 'Classic Shape';
    }

    detectSides() {
      // Check if variant has "Double Sided" in the title or options
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        // Check for double-sided indicators
        if (variantTitle.includes('double') ||
            variantTitle.includes('2-sided') ||
            variantTitle.includes('two sided') ||
            variantTitle.includes('2 sided')) {
          log('Detected double-sided from variant title');
          return 'double';
        }
      }

      // Check variant options
      const options = document.querySelectorAll('[name^="options["]');
      for (const option of options) {
        const value = option.value ? option.value.toLowerCase() : '';
        if (value.includes('double') ||
            value.includes('2-sided') ||
            value.includes('two sided') ||
            value.includes('2 sided')) {
          log('Detected double-sided from variant option');
          return 'double';
        }
      }

      // Default to single
      log('Detected single-sided (default)');
      return 'single';
    }

    watchVariantChanges() {
      // Watch for variant selector changes
      const variantSelectors = document.querySelectorAll('select[name="id"], [name^="options["]');
      variantSelectors.forEach(select => {
        select.addEventListener('change', () => {
          // Use setTimeout to ensure card-variants-optimized.js updates complete first
          setTimeout(async () => {
            let needsRerender = false;

            // Check for sides changes
            const newSides = this.detectSides();
            if (newSides !== this.state.sides) {
              const oldSides = this.state.sides;
              this.state.sides = newSides;
              log(`Sides updated from ${oldSides} to: ${newSides}`);

              // If switching from double to single, clear back file and switch to front
              if (oldSides === 'double' && newSides === 'single') {
                this.state.files.back = null;
                this.state.uploads.back = null;
                this.state.uploadProgress.back = 0;
                this.state.activeTab = 'front';
                log('Cleared back side files and switched to front tab');
              }

              needsRerender = true;
            }

            // Check for shape changes
            const newShape = this.detectShape();
            if (newShape !== this.state.shape) {
              const oldShape = this.state.shape;
              this.state.shape = newShape;
              log(`Shape updated from ${oldShape} to: ${newShape}`);

              // Regenerate previews with new mask for any uploaded files (async)
              await this.regeneratePreviewsForShapeChange();

              needsRerender = true;
            }

            // Re-render if anything changed
            if (needsRerender) {
              this.render();
              this.attachEventListeners();
            }
          }, 50); // Small delay to let card-variants-optimized.js update first
        });
      });
    }

    watchDesignMethodChanges() {
      // Listen for design method toggle changes via StateManager
      if (!this.stateManager) {
        log('⚠️ StateManager not available, cannot watch design method changes');
        return;
      }

      this.stateManager.on('design:method:changed', (event) => {
        const newMethod = event.detail;
        log(`📡 Design method changed to: ${newMethod}`);

        if (newMethod === 'pitchprint') {
          // Switched to PitchPrint - hide file uploader
          log('🙈 Hiding file uploader (PitchPrint selected)');
          if (this.container) {
            this.container.style.display = 'none';
          }

          // Clear uploaded files when switching away
          this.reset();
        } else if (newMethod === 'file_upload') {
          // Switched to file upload - show file uploader
          log('✅ Showing file uploader (File upload selected)');
          if (this.container) {
            this.container.style.display = 'block';
          }
        }
      });

      log('✅ Design method change watcher initialized');
    }

    renderLargePreview() {
      const activeSide = this.state.activeTab;
      const hasFile = this.state.uploads[activeSide] !== null;
      const previewSrc = hasFile ? this.state.uploads[activeSide].previewUrl : '';

      // Skeleton shape class based on current shape
      const shapeClass = this.state.shape.toLowerCase().replace(/\s+/g, '-');

      return `
        <div class="large-preview-area">
          <div class="preview-header">
            <h3 class="preview-title">Preview</h3>
          </div>

          <div class="large-preview-container">
            ${hasFile ? `
              <img class="large-preview-image static-preview" src="${previewSrc}" alt="Preview">
            ` : `
              <div class="large-preview-skeleton">
                <div class="skeleton-instructions">
                  <h4 class="skeleton-title">Upload Your Artwork</h4>
                  <p class="skeleton-description">Use the upload buttons below to add your design files</p>
                  <p class="skeleton-formats">Supports PNG, JPG, PDF • Max 300MB</p>
                </div>
              </div>
            `}
          </div>

          ${this.state.sides === 'double' ? `
            <div class="preview-footer">
              <div class="preview-tabs">
                <button class="preview-tab ${this.state.activeTab === 'front' ? 'active' : ''}" data-side="front">Side 1</button>
                <button class="preview-tab ${this.state.activeTab === 'back' ? 'active' : ''}" data-side="back">Side 2</button>
              </div>
            </div>
          ` : ''}
        </div>
      `;
    }

    renderUploadRows() {
      const renderRow = (side, label) => {
        const hasFile = this.state.files[side] !== null;
        const upload = this.state.uploads[side];
        const progress = this.state.uploadProgress[side];
        const isUploading = hasFile && progress < 100;
        const isUploaded = hasFile && upload !== null;

        const rowClass = hasFile ? (isUploaded ? 'uploaded-file-item success' : 'uploaded-file-item uploading') : 'uploaded-file-item empty';

        return `
          <div class="upload-row-wrapper">
            <div class="${rowClass}" data-side="${side}">
              ${!hasFile ? `
                <!-- Empty state with new layout -->
                <div class="file-empty-left">
                  <div class="file-thumbnail skeleton">
                    <svg class="skeleton-icon" width="40" height="32" viewBox="0 0 40 32" fill="currentColor">
                      <path d="M33,0H7C3.134,0,0,3.134,0,7v18c0,3.866,3.134,7,7,7h26c3.866,0,7-3.134,7-7V7c0-3.866-3.134-7-7-7ZM2,7c0-2.761,2.239-5,5-5h26c2.761,0,5,2.239,5,5v16.61l-9.29-9.32c-.189-.187-.444-.292-.71-.29h0c-.266-.002-.521.103-.71.29l-10.29,10.3-6.29-6.3c-.389-.392-1.022-.395-1.414-.006-.002.002-.004.004-.006.006l-7.24,7.24c-.027-.176-.043-.353-.05-.53V7ZM33,30H7c-1.722-.006-3.321-.897-4.23-2.36l7.23-7.23,6.29,6.3c.389.392,1.022.395,1.414.006.002-.002.004-.004.006-.006l10.29-10.29,9.82,9.84c-.572,2.196-2.551,3.732-4.82,3.74Z"/>
                      <path d="M14,14c2.209,0,4-1.791,4-4s-1.791-4-4-4-4,1.791-4,4,1.791,4,4,4ZM14,8c1.105,0,2,.895,2,2s-.895,2-2,2-2-.895-2-2,.895-2,2-2Z"/>
                    </svg>
                  </div>
                  <div class="file-empty-info">
                    <h4 class="upload-row-label">${label}</h4>
                    <small class="file-formats-inline">PNG, JPG, PDF • Max 300MB</small>
                  </div>
                </div>
                <div class="file-empty-right">
                  <button class="file-upload-trigger" data-side="${side}">
                    <svg class="upload-icon" width="46" height="34" viewBox="0 0 92 68" fill="currentColor">
                      <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                    </svg>
                    Click to Upload
                  </button>
                  <small class="file-dropzone-hint">or drop files here</small>
                </div>

                <!-- Dropzone overlay for drag feedback -->
                <div class="dropzone-overlay">
                  <svg class="upload-icon" width="48" height="48" viewBox="0 0 92 68" fill="currentColor">
                    <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                  </svg>
                  <span>Drop file here</span>
                </div>
              ` : `
                <!-- Uploaded state -->
                <div class="file-thumbnail">
                  ${upload ? `
                    <img src="${upload.thumbnailUrl || upload.previewUrl}" alt="${label}">
                  ` : `
                    <div class="skeleton">
                      <svg class="skeleton-icon" width="40" height="32" viewBox="0 0 40 32" fill="currentColor">
                        <path d="M33,0H7C3.134,0,0,3.134,0,7v18c0,3.866,3.134,7,7,7h26c3.866,0,7-3.134,7-7V7c0-3.866-3.134-7-7-7ZM2,7c0-2.761,2.239-5,5-5h26c2.761,0,5,2.239,5,5v16.61l-9.29-9.32c-.189-.187-.444-.292-.71-.29h0c-.266-.002-.521.103-.71.29l-10.29,10.3-6.29-6.3c-.389-.392-1.022-.395-1.414-.006-.002.002-.004.004-.006.006l-7.24,7.24c-.027-.176-.043-.353-.05-.53V7ZM33,30H7c-1.722-.006-3.321-.897-4.23-2.36l7.23-7.23,6.29,6.3c.389.392,1.022.395,1.414.006.002-.002.004-.004.006-.006l10.29-10.29,9.82,9.84c-.572,2.196-2.551,3.732-4.82,3.74Z"/>
                        <path d="M14,14c2.209,0,4-1.791,4-4s-1.791-4-4-4-4,1.791-4,4,1.791,4,4,4ZM14,8c1.105,0,2,.895,2,2s-.895,2-2,2-2-.895-2-2,.895-2,2-2Z"/>
                      </svg>
                    </div>
                  `}
                </div>

                <div class="file-info">
                  <div class="file-header">
                    <svg class="file-check" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="file-name">${this.state.files[side].name}</span>
                  </div>
                  <div class="file-meta">
                    <span class="file-size">Size: ${(this.state.files[side].size / 1024 / 1024).toFixed(2)}MB</span>
                    ${isUploaded ? '<span class="file-status">✓ Uploaded</span>' : ''}
                  </div>
                  ${isUploading ? `
                    <div class="file-progress-bar">
                      <span class="file-progress-fill" style="width: ${progress}%"></span>
                    </div>
                    <span class="file-progress-text">${Math.round(progress)}%</span>
                  ` : ''}
                </div>
              `}

              ${hasFile ? `
                <div class="file-actions">
                  <button class="file-replace-btn" data-side="${side}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 4v6h6M23 20v-6h-6" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Replace
                  </button>
                  <button class="file-remove-btn" data-side="${side}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3,6 5,6 21,6" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              ` : ''}

              <input type="file" id="file-input-${side}" accept="image/png,image/jpeg,application/pdf" class="upload-file-input" style="display: none;">
            </div>
          </div>
        `;
      };

      return `
        <div class="uploaded-files-list">
          ${renderRow('front', 'Side 1')}
          ${this.state.sides === 'double' ? renderRow('back', 'Side 2') : ''}
        </div>
      `;
    }

    renderTemplateHelp() {
      // Use templates from product metafield if available, otherwise fallback to hardcoded defaults
      let templates = [];

      if (window.PRODUCT_DESIGN_TEMPLATES && window.PRODUCT_DESIGN_TEMPLATES.length > 0) {
        // Use dynamic templates from metafield
        templates = window.PRODUCT_DESIGN_TEMPLATES.map(template => ({
          name: template.name,
          specs: template.specs,
          url: template.url
        }));
        log('Using dynamic templates from product metafield:', templates);
      } else {
        // Fallback to hardcoded templates
        templates = [
          {
            name: 'Circle Shape',
            specs: '12" diameter • 1512 x 1512 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Circle_Template.png?v=1234'
          },
          {
            name: 'Classic Shape',
            specs: '12" x 12" • 1512 x 1512 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Classic_Template.png?v=1234'
          },
          {
            name: 'Longboard Shape',
            specs: '36" x 10" • 2664 x 728 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Longboard_Template.png?v=1234'
          },
          {
            name: 'Longboard Mini',
            specs: '20" x 8" • 1512 x 594 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Longboard_Mini_Template.png?v=1234'
          }
        ];
        log('Using fallback hardcoded templates');
      }

      // If no templates available, don't render the section
      if (templates.length === 0) {
        return '';
      }

      return `
        <div class="template-help-section">
          <button class="template-help-header" type="button">
            <div class="template-help-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Need help? Download design templates</span>
            </div>
            <svg class="template-help-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          <div class="template-help-content">
            <div class="template-help-body">
              <div class="template-downloads">
                <h4>Download Templates</h4>
                <div class="template-grid">
                  ${templates.map(template => `
                    <a href="${template.url}" download="${template.name.replace(/\s+/g, '_')}_Template" class="template-download-btn">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <div class="template-info">
                        <span class="template-name">${template.name}</span>
                        <span class="template-specs">${template.specs}</span>
                      </div>
                    </a>
                  `).join('')}
                </div>
              </div>

              <div class="template-tips">
                <h4>Design Tips</h4>
                <ul>
                  <li><strong>Resolution:</strong> Use 300 DPI for best print quality</li>
                  <li><strong>Safe Zone:</strong> Keep important elements within the inner guide area</li>
                  <li><strong>Bleed:</strong> Extend your design to the outer edges</li>
                  <li><strong>File Format:</strong> PNG or JPG recommended, PDF also accepted</li>
                  <li><strong>File Size:</strong> Maximum 300MB per file</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    render() {
      this.container.innerHTML = `
        <div class="file-upload-wrapper">
          <!-- Mode Selection -->
          <div class="upload-mode-selector">
            <div class="mode-option ${this.state.mode === 'upload_now' ? 'active' : ''}">
              <input type="radio" id="mode-upload-now" name="upload_mode" value="upload_now" ${this.state.mode === 'upload_now' ? 'checked' : ''}>
              <label for="mode-upload-now">
                <svg class="mode-icon" width="46" height="34" viewBox="0 0 92 68" fill="currentColor">
                  <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                </svg>
                <span>Upload Files Now</span>
              </label>
            </div>
            <div class="mode-option ${this.state.mode === 'send_later' ? 'active' : ''}">
              <input type="radio" id="mode-send-later" name="upload_mode" value="send_later" ${this.state.mode === 'send_later' ? 'checked' : ''}>
              <label for="mode-send-later">
                <svg class="mode-icon" width="46" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>Send Files Later (via email)</span>
              </label>
            </div>
          </div>

          <!-- Upload Controls (visible only for 'upload_now') -->
          <div class="upload-controls" style="display: ${this.state.mode === 'upload_now' ? 'block' : 'none'}">
            ${this.renderLargePreview()}
            ${this.renderUploadRows()}
            ${this.renderTemplateHelp()}
          </div>

          <!-- Send Later Notice -->
          <div class="send-later-notice" style="display: ${this.state.mode === 'send_later' ? 'flex' : 'none'}">
            <p>You'll receive an email after checkout with instructions to upload your design files.</p>
          </div>

          <!-- Error Messages -->
          <div class="upload-errors" style="display: none;"></div>
        </div>
      `;
    }

    attachEventListeners() {
      // Mode selection
      this.container.querySelectorAll('input[name="upload_mode"]').forEach(radio => {
        radio.addEventListener('change', (e) => this.handleModeChange(e.target.value));
      });

      // Preview tab switching (Side 1 / Side 2)
      this.container.querySelectorAll('.preview-tab').forEach(tab => {
        tab.addEventListener('click', (e) => {
          const side = e.currentTarget.dataset.side;
          this.handleTabSwitch(side);
        });
      });

      // File inputs and upload triggers
      ['front', 'back'].forEach(side => {
        const input = this.container.querySelector(`#file-input-${side}`);
        if (input) {
          input.addEventListener('change', (e) => this.handleFileSelect(e, side));
        }

        // Upload trigger button clicks (for empty state)
        const uploadTrigger = this.container.querySelector(`.file-upload-trigger[data-side="${side}"]`);
        if (uploadTrigger) {
          uploadTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            input?.click();
          });
        }

        // Replace button clicks
        const replaceBtn = this.container.querySelector(`.file-replace-btn[data-side="${side}"]`);
        if (replaceBtn) {
          replaceBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            input?.click();
          });
        }

        // Remove file button
        const removeBtn = this.container.querySelector(`.file-remove-btn[data-side="${side}"]`);
        if (removeBtn) {
          removeBtn.addEventListener('click', () => this.handleRemoveFile(side));
        }

        // Drag and drop on file rows
        const fileRow = this.container.querySelector(`.uploaded-file-item[data-side="${side}"]`);
        if (fileRow) {
          fileRow.addEventListener('dragover', (e) => this.handleDragOver(e));
          fileRow.addEventListener('dragleave', (e) => this.handleDragLeave(e));
          fileRow.addEventListener('drop', (e) => this.handleDrop(e, side));
        }
      });

      // Template help toggle
      const templateHelpHeader = this.container.querySelector('.template-help-header');
      if (templateHelpHeader) {
        templateHelpHeader.addEventListener('click', () => {
          const section = this.container.querySelector('.template-help-section');
          section.classList.toggle('expanded');
        });
      }
    }

    handleTabSwitch(side) {
      this.state.activeTab = side;
      log('Tab switched to:', side);
      this.render();
      this.attachEventListeners();
    }

    handleModeChange(mode) {
      this.state.mode = mode;
      log('Mode changed to:', mode);

      // Update UI
      const uploadControls = this.container.querySelector('.upload-controls');
      const sendLaterNotice = this.container.querySelector('.send-later-notice');

      if (mode === 'upload_now') {
        uploadControls.style.display = 'block';
        sendLaterNotice.style.display = 'none';
      } else {
        uploadControls.style.display = 'none';
        sendLaterNotice.style.display = 'flex';
      }

      // Update mode options styling
      this.container.querySelectorAll('.mode-option').forEach(option => {
        option.classList.toggle('active', option.querySelector('input').value === mode);
      });

      this.emitStateChange();
    }

    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('drag-over');
    }

    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('drag-over');
    }

    handleDrop(e, side) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('drag-over');

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0], side);
      }
    }

    handleFileSelect(e, side) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0], side);
      }
    }

    async processFile(file, side) {
      log('Processing file:', file.name, 'for side:', side);

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.showError(validation.error);
        return;
      }

      this.state.files[side] = file;
      this.state.uploadProgress[side] = 0;

      // Re-render to show uploading state
      this.render();
      this.attachEventListeners();

      // OPTIMIZATION: Start upload and preview generation in parallel for faster uploads
      // Large files (especially 300MB PDFs) can upload while preview is being generated
      try {
        this.showProgress(side, 0, 'Starting upload...');

        // Start BOTH operations in parallel (don't wait for preview before uploading)
        const uploadPromise = this.uploadFile(file, null, side);

        const previewPromise = (async () => {
          // Show appropriate message based on file size
          const fileSizeMB = file.size / (1024 * 1024);
          const message = fileSizeMB > 50
            ? `Processing ${Math.round(fileSizeMB)}MB file...`
            : 'Generating preview...';

          this.showProgress(side, 5, message);

          // Generate masked preview and raw thumbnail in parallel
          // Use higher resolution for thumbnail (400px) for better quality in display
          const [preview, rawThumbnail] = await Promise.all([
            this.previewRenderer.generatePreview(file, this.state.shape),
            this.previewRenderer.generateRawThumbnail(file, 400)
          ]);

          log('Preview and raw thumbnail generated');

          // Store both preview (masked) and thumbnail (raw)
          this.state.uploads[side] = {
            ...(this.state.uploads[side] || {}),
            thumbnailUrl: rawThumbnail.dataUrl, // Raw artwork thumbnail
            previewUrl: preview.dataUrl         // Masked preview
          };

          // Re-render to show thumbnail while uploading continues
          this.render();
          this.attachEventListeners();

          return { preview, rawThumbnail };
        })();

        // Wait for both upload and preview generation to complete
        const [uploadResult, previewResult] = await Promise.all([uploadPromise, previewPromise]);

        // Switch to the uploaded side to show the preview
        this.state.activeTab = side;
        log(`Switched to ${side} side to show uploaded preview`);

        // Final re-render after both complete
        this.render();
        this.attachEventListeners();

      } catch (error) {
        console.error('[FileUploader] Error processing file:', error);
        this.showError(`Failed to process ${file.name}: ${error.message}`);
        this.state.files[side] = null;
        this.state.uploads[side] = null;
        this.render();
        this.attachEventListeners();
      }
    }

    validateFile(file) {
      const maxSize = 300 * 1024 * 1024; // 300MB
      const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];

      if (!allowedTypes.includes(file.type)) {
        return {
          valid: false,
          error: `Invalid file type. Please upload PNG, JPG, or PDF files.`
        };
      }

      if (file.size > maxSize) {
        return {
          valid: false,
          error: `File too large. Maximum size is 300MB.`
        };
      }

      return { valid: true };
    }

    async uploadFile(file, preview, side) {
      try {
        log('Starting upload for:', file.name);

        // Step 1: Get presigned URL
        this.showProgress(side, 10, 'Preparing upload...');

        const presignResponse = await this.getPresignedUrl(file, side);

        log('Got presigned URL:', presignResponse.uploadId);

        // Step 2: Upload original file to S3
        this.showProgress(side, 30, 'Uploading file...');

        await this.uploadToS3(presignResponse.uploadUrl, file, (progress) => {
          this.showProgress(side, 30 + (progress * 0.6), 'Uploading...');
        });

        // Step 3: Skip preview upload for now (preview is null when running in parallel)
        // Preview will be available after parallel generation completes
        this.showProgress(side, 90, 'Finalizing...');

        // Step 4: Confirm upload (preview URL will be added later if needed)
        await this.confirmUpload(presignResponse.uploadId, side, file.size, null);

        this.showProgress(side, 100, 'Complete!');

        // Store upload data - preserve existing preview/thumbnail if already set
        const currentPreview = this.state.uploads[side]?.previewUrl;
        const currentThumbnail = this.state.uploads[side]?.thumbnailUrl;

        this.state.uploads[side] = {
          ...(this.state.uploads[side] || {}),
          uploadId: presignResponse.uploadId,
          originalUrl: presignResponse.originalKey,
          fileKey: presignResponse.originalKey, // S3 key for the uploaded file
          // Keep existing preview/thumbnail if already generated, otherwise set to null
          previewUrl: currentPreview || null,
          thumbnailUrl: currentThumbnail || null,
          fileName: file.name,
          fileSize: file.size
        };

        this.emitStateChange();

        log('Upload complete:', presignResponse.uploadId);
        return presignResponse; // Return for parallel processing

      } catch (error) {
        console.error('[FileUploader] Upload failed:', error);
        this.showError(`Upload failed: ${error.message}`);
        throw error;
      }
    }

    async getPresignedUrl(file, side) {
      const response = await fetch(`${API_ENDPOINT}/api/uploads/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': 'studio.popsigns.co'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          customerId: this.getCustomerId(),
          shape: this.state.shape,
          side: side
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.statusText}`);
      }

      return await response.json();
    }

    async uploadToS3(presignedUrl, file, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = e.loaded / e.total;
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    }

    async uploadPreviewToS3(uploadId, previewBlob) {
      // For now, return a placeholder - implement S3 upload for preview
      // In production, would get another presigned URL for preview
      return `https://placeholder.com/preview-${uploadId}.png`;
    }

    async confirmUpload(uploadId, side, fileSize, previewUrl) {
      const response = await fetch(`${API_ENDPOINT}/api/uploads/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': 'studio.popsigns.co'
        },
        body: JSON.stringify({
          uploadId,
          side,
          fileSize,
          previewUrl,
          maskApplied: this.state.shape
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to confirm upload: ${response.statusText}`);
      }

      return await response.json();
    }

    getCustomerId() {
      // Get customer ID from Shopify or generate temporary ID
      // Always return as string for DynamoDB compatibility
      const customerId = window.ShopifyAnalytics?.meta?.page?.customerId;
      return customerId ? String(customerId) : `temp-${Date.now()}`;
    }

    showProgress(side, percent, text) {
      this.state.uploadProgress[side] = percent;

      // Update progress bar if row exists
      const fileRow = this.container.querySelector(`.uploaded-file-item[data-side="${side}"]`);
      if (fileRow) {
        const progressFill = fileRow.querySelector('.file-progress-fill');
        const progressText = fileRow.querySelector('.file-progress-text');

        if (progressFill) progressFill.style.width = `${percent}%`;
        if (progressText) progressText.textContent = text || `${Math.round(percent)}%`;
      }
    }

    handleRemoveFile(side) {
      this.state.files[side] = null;
      this.state.uploads[side] = null;
      this.state.uploadProgress[side] = 0;
      this.render();
      this.attachEventListeners();
      this.emitStateChange();
    }

    async regeneratePreviewsForShapeChange() {
      // Regenerate previews for all uploaded files with the new shape mask
      const sides = ['front', 'back'];

      // Show loading overlay on preview area
      this.showPreviewLoading(true, 'Regenerating previews for new shape...');

      for (const side of sides) {
        // Only regenerate if we have a file uploaded
        if (this.state.files[side]) {
          log(`Regenerating preview for ${side} with new shape: ${this.state.shape}`);

          try {
            // Update loading message with current side
            const fileSizeMB = this.state.files[side].size / (1024 * 1024);
            const message = fileSizeMB > 50
              ? `Regenerating ${side} preview (${Math.round(fileSizeMB)}MB)...`
              : `Regenerating ${side} preview...`;
            this.showPreviewLoading(true, message);

            // Generate new masked preview (thumbnail stays as raw artwork)
            const preview = await this.previewRenderer.generatePreview(
              this.state.files[side],
              this.state.shape
            );

            // Update only the preview URL (keep raw thumbnail unchanged)
            if (this.state.uploads[side]) {
              this.state.uploads[side].previewUrl = preview.dataUrl;
              // thumbnailUrl stays as raw artwork - don't update it
            }

            log(`Preview regenerated for ${side}`);
          } catch (error) {
            console.error(`[FileUploader] Failed to regenerate preview for ${side}:`, error);
          }
        }
      }

      // Hide loading overlay
      this.showPreviewLoading(false);
    }

    showPreviewLoading(show, message = 'Processing...') {
      const previewContainer = this.container.querySelector('.large-preview-container');
      if (!previewContainer) return;

      let loadingOverlay = previewContainer.querySelector('.preview-loading-overlay');

      if (show) {
        if (!loadingOverlay) {
          // Create loading overlay
          loadingOverlay = document.createElement('div');
          loadingOverlay.className = 'preview-loading-overlay';
          loadingOverlay.innerHTML = `
            <div class="preview-loading-content">
              <div class="preview-loading-spinner"></div>
              <div class="preview-loading-text">${message}</div>
            </div>
          `;
          previewContainer.appendChild(loadingOverlay);
        } else {
          // Update message
          const textEl = loadingOverlay.querySelector('.preview-loading-text');
          if (textEl) textEl.textContent = message;
          loadingOverlay.style.display = 'flex';
        }
      } else {
        if (loadingOverlay) {
          loadingOverlay.style.display = 'none';
        }
      }
    }

    showError(message) {
      const errorsEl = this.container.querySelector('.upload-errors');
      if (!errorsEl) return;

      errorsEl.textContent = message;
      errorsEl.style.display = 'block';

      setTimeout(() => {
        errorsEl.style.display = 'none';
      }, 5000);
    }

    emitStateChange() {
      if (this.stateManager) {
        const uploadState = {
          mode: this.state.mode,
          sides: this.state.sides,
          shape: this.state.shape,
          uploads: this.state.uploads,
          isComplete: this.isUploadComplete()
        };

        // Emit event for backward compatibility
        this.stateManager.emit('file:upload:state-changed', uploadState);

        // ALWAYS update upload mode in StateManager (even when not complete)
        // This ensures mode changes are reflected immediately
        this.stateManager.setUploadMode(this.state.mode);

        // Update StateManager's upload state based on completion
        if (this.isUploadComplete()) {
          const uploadData = {
            uploadId: this.state.uploads.front?.uploadId || this.state.uploads.back?.uploadId,
            files: {
              front: this.state.uploads.front?.fileKey,
              back: this.state.uploads.back?.fileKey
            },
            previews: {
              front: this.state.uploads.front?.previewUrl,
              back: this.state.uploads.back?.previewUrl
            },
            shape: this.state.shape,
            sides: this.state.sides,
            mode: this.state.mode
          };
          this.stateManager.setUploadData(uploadData);
        } else {
          // Clear upload data when switching to upload_now without files
          // This prevents validation from passing when user hasn't uploaded anything
          this.stateManager.clearUploadData();
        }
      }
    }

    isUploadComplete() {
      if (this.state.mode === 'send_later') return true;

      if (this.state.sides === 'single') {
        return this.state.uploads.front !== null;
      } else {
        return this.state.uploads.front !== null && this.state.uploads.back !== null;
      }
    }

    getState() {
      return {
        ...this.state,
        isComplete: this.isUploadComplete()
      };
    }

    reset() {
      log('Resetting file uploader to initial state');

      // Clear all file and upload state
      this.state.files = {
        front: null,
        back: null
      };
      this.state.uploads = {
        front: null,
        back: null
      };
      this.state.uploadProgress = {
        front: 0,
        back: 0
      };

      // Reset to default tab
      this.state.activeTab = 'front';

      // Keep mode and sides settings as user preference
      // this.state.mode and this.state.sides are intentionally not reset

      // Clear StateManager upload data
      if (this.stateManager && typeof this.stateManager.clearUploadData === 'function') {
        this.stateManager.clearUploadData();
      }

      // Hide any loading overlays
      this.showPreviewLoading(false);

      // Hide any error messages
      const errorsEl = this.container.querySelector('.upload-errors');
      if (errorsEl) {
        errorsEl.style.display = 'none';
        errorsEl.textContent = '';
      }

      // Re-render UI to show empty state
      this.render();
      this.attachEventListeners();

      log('File uploader reset complete');
    }
  }

  // Export to window
  window.CustomFileUploader = CustomFileUploader;

  log('Custom file uploader loaded');
})();
